import { roundNumber } from "src/utils/common-utils";
import { ESG_ROW_KEYS } from "../constants";


function getNumberExternalAnswers(entries, areaKey, questionKey) {
    return entries.filter((entry) => entry[areaKey]?.[questionKey]?.score)
        .length;
}

// returns admin score or if empty, internal score or "n/a"
function convertAdminScore(adminScore, emptyVal = "n/a", weight) {
    if (+weight === 0) return 0
    if (isNaN(adminScore)) return emptyVal;
    return +adminScore
}

function getAverageExternalScore(entries, areaKey, questionKey) {
    const filteredEntries = entries.filter(
        (entry) => entry[areaKey]?.[questionKey]?.score && entry[areaKey]?.[questionKey]?.score !== "n/a"
    );
    const numAnswers = filteredEntries.length;
    if (!numAnswers) return "n/a";
    if (numAnswers < 4) {
        const total = filteredEntries.reduce((acc, entry) => {
            return acc + (+entry[areaKey]?.[questionKey]?.score || 0);
        }, 0);
        return roundNumber(total / numAnswers, 2);
    } else {
        const answers = filteredEntries
            .map((entry) => +entry[areaKey]?.[questionKey]?.score)
            .sort((a, b) => a - b);

        const middle = Math.floor(answers.length / 2);
        const middle3 = [
            answers[middle - 1],
            answers[middle],
            answers[middle + 1],
        ];

        const averageMiddle3 = roundNumber(
            middle3.reduce((acc, item) => acc + item, 0) / 3,
            2
        );
        return averageMiddle3;
    }
}

function calculateFinalScore(adminScore, externalScore) {
    if (externalScore === "n/a") return adminScore;
    if (externalScore >= adminScore) return adminScore;

    return roundNumber((+adminScore + +externalScore) / 2, 2);
}


function getMaxPossibleScore(question) {

    let maxPossibleScore = 0;
    question.answers.forEach((a) => {
        if (a.value > maxPossibleScore) {
            maxPossibleScore = a.value;
        }
    });
    return maxPossibleScore;
}

const filterQuestionRows = (rows) => rows.filter(row => !row._classes?.includes("table-sector") && !row._classes?.includes("table-total"))

const calculateTotalPossible = (rows) => rows.reduce((acc, r) => acc + +r.weight * (r.maxScore || 0), 0)


function calculateTotals(rows, onlyInArea, type) {

    let getScoreFn = null
    switch (type) {
        case "self":
            getScoreFn = (row) => (+row[ESG_ROW_KEYS.selfValue] || 0)
            break;
        case "admin":
            getScoreFn = (row) => {
                if (row[ESG_ROW_KEYS.adminScore] && !isNaN(+row[ESG_ROW_KEYS.adminScore])) {
                    return +row[ESG_ROW_KEYS.adminScore]
                } else if (row[ESG_ROW_KEYS.adminScore] === 0) {
                    return 0
                } else if (row[ESG_ROW_KEYS.selfValue] && !isNaN(+row[ESG_ROW_KEYS.selfValue])) {
                    return +row[ESG_ROW_KEYS.selfValue]
                } else {
                    return 0
                }
            }
            break;
        case "external":
            getScoreFn = (row) => +row["external-average"]
            break;
        case "final":
            getScoreFn = (row) => +row["final-score"]
            break;
        default:
            break;
    }

    let filteredRows = rows
    if (onlyInArea) {
        filteredRows = rows.filter(row => row.area === onlyInArea)
    }

    const questionRows = filterQuestionRows(filteredRows)

    const scores = questionRows
        .map(row => getScoreFn(row) * row[ESG_ROW_KEYS.weight])

    const totalPossible = calculateTotalPossible(questionRows)

    const totalScores = roundNumber(scores.reduce((acc, score) => acc + (+score || 0), 0), 2)

    const totalPercentage = Math.round((totalScores / totalPossible) * 100);

    return {
        totalText: `${totalScores} / ${totalPossible} = ${totalPercentage}%`,
        totalPercentage: totalPercentage,
        totalScore: totalScores,
        totalPossible,
    }
}

function calculateSelfTotals(rows, onlyInArea) {
    return calculateTotals(rows, onlyInArea, "self")
}

function calculateAdminTotals(rows, onlyInArea) {
    return calculateTotals(rows, onlyInArea, "admin")
}

function calculateExternalTotals(rows, onlyInArea) {
    return calculateTotals(rows, onlyInArea, "external")
}

function calculateFinalTotals(rows, onlyInArea) {
    return calculateTotals(rows, onlyInArea, "final")
}


// function calculateExternalTotals(rows, onlyInArea) {

//     let filteredRows = rows
//     if (onlyInArea) {
//         filteredRows = rows.filter(row => row.area === onlyInArea)
//     }

//     const questionRows = filterQuestionRows(filteredRows)

//     const rowsWithScores = questionRows
//         .filter((row) => row["external-average"] !== "n/a")

//     const totalScores = roundNumber(rowsWithScores.reduce(
//         (acc, row) => acc + +row["external-average"] * +row.weight,
//         0
//     ), 2);
//     const totalPossible = calculateTotalPossible(rowsWithScores)

//     const totalPercentage = Math.round((totalScores / totalPossible) * 100);

//     return {
//         externalTotalText: totalPossible ? `${totalScores} / ${totalPossible} = ${totalPercentage}%` : "n/a",
//         externalTotalPercentage: totalPercentage,
//         externalTotalScore: totalScores,
//         totalPossibleScore: totalPossible,
//     }
// }


function getRomanNumeral(num) {
    switch (num) {
        case 1:
            return "I";
        case 2:
            return "II";
        case 3:
            return "III";
        case 4:
            return "IV";
        case 5:
            return "V";
        case 6:
            return "VI";
        case 7:
            return "VII";
        case 8:
            return "VIII";
        case 9:
            return "IX";
        case 10:
            return "X";
        default:
            return "";
    }
}

export {
    calculateAdminTotals,
    calculateExternalTotals,
    calculateFinalScore,
    calculateSelfTotals,
    calculateFinalTotals,
    convertAdminScore,
    getAverageExternalScore,
    getMaxPossibleScore,
    getNumberExternalAnswers,
    getRomanNumeral
};
